import React, { useState } from 'react'
import PropTypes from 'prop-types'
import uniqBy from 'lodash/uniqBy'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { IconButton, Stack, Tooltip } from '@mui/material'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import { Plus } from '@styled-icons/heroicons-outline/Plus'

import { get } from 'utils/lodash'
import { setAddContactState } from 'slices/customer/contactsSlice'
import { canUpdateCustomerManager } from 'data/permissions/permissionsSelectors'

import T from 'T'
import HHAccordion from 'components/common/HHAccordion'
import EventBlocker from 'components/common/EventBlocker'

import ContactsListing from './ContactsListing'
import AddEditContactDialog from './AddEditContactDialog'
import { getAddModel } from './settings'

const Contacts = ({ customerDetails }) => {
  const dispatch = useDispatch()
  const updateCustomerManager = useSelector(canUpdateCustomerManager, shallowEqual)
  const isOpenAddContactDialog = useSelector(state => get(state, 'Contacts.addContact.isOpen', false), shallowEqual)
  const initialMethodValue = useSelector(state => get(state, 'Contacts.addContact.initialMethodValue', ''), shallowEqual)
  const initialFirstName = useSelector(state => get(state, 'Contacts.addContact.initialFirstName', ''), shallowEqual)
  const initialLastName = useSelector(state => get(state, 'Contacts.addContact.initialLastName', ''), shallowEqual)

  // bulk expand/collapse signal. version > 0 means a broadcast has fired; cards sync
  // on each version change, then can still be toggled individually.
  const [bulkExpansion, setBulkExpansion] = useState({ open: true, version: 0 })
  const toggleBulkExpansion = () => setBulkExpansion(state => ({ open: !state.open, version: state.version + 1 }))

  const accountContacts = get(customerDetails, 'contacts', [])
  const locationContacts = get(customerDetails, 'locations', []).map(location => get(location, 'contacts', []))
  const mergeContacts = [...accountContacts, ...locationContacts.flat()]
  // Remove duplicates
  const uniqueContacts = uniqBy(mergeContacts, contact => contact.id)

  const addModel = getAddModel(initialFirstName, initialLastName, initialMethodValue)

  const hasContacts = uniqueContacts.length > 0
  const collapseAllLabel = bulkExpansion.open ? T.COLLAPSE_ALL : T.EXPAND_ALL

  const handleOpenAddContact = () => {
    dispatch(setAddContactState({ isOpen: true, initialFirstName: '', initialLastName: '', initialMethodValue: '' }))
  }

  // Tight cluster of action icons on the right (mirrors the Customer Portal accordion
  // pattern): collapse/expand toggle immediately to the LEFT of the + (add) icon.
  // EventBlocker stops the click from toggling the accordion itself.
  const customActions = (
    <EventBlocker>
      <Stack direction="row" gap={0.5}>
        {hasContacts && (
          <Tooltip arrow title={collapseAllLabel}>
            <IconButton sx={{ color: 'text.secondary' }} onClick={toggleBulkExpansion} aria-label={collapseAllLabel}>
              {bulkExpansion.open ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </IconButton>
          </Tooltip>
        )}
        {updateCustomerManager && (
          <IconButton sx={{ color: 'text.secondary' }} onClick={handleOpenAddContact} aria-label="Add contact">
            <Plus width={20} height={20} />
          </IconButton>
        )}
      </Stack>
    </EventBlocker>
  )

  return (
    <>
      <HHAccordion
        isExpandCollapseHeightSame
        withNewColors
        isExpanded
        summary={T.CONTACTS}
        showActionIcon={false}
        showCustomActions
        customActionContent={customActions}
        sx={{ position: 'inherit' }}
      >
        <ContactsListing contacts={uniqueContacts} customerDetails={customerDetails} showActionMenu bulkExpansion={bulkExpansion} />
      </HHAccordion>

      {isOpenAddContactDialog && (
        <AddEditContactDialog
          isOpen={isOpenAddContactDialog}
          accountId={get(customerDetails, 'id')}
          contact={initialMethodValue ? addModel : null}
          onClose={() =>
            dispatch(
              setAddContactState({
                isOpen: false,
                accountId: '',
                initialFirstName: '',
                initialLastName: '',
                initialMethodValue: '',
                parent: '',
              })
            )
          }
        />
      )}
    </>
  )
}

Contacts.propTypes = {
  customerDetails: PropTypes.object.isRequired,
}

export default Contacts
